import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_INSTITUTIONPROCEDURES_LOAD_BY_INSTITUTION_ID,
    ACTION_INSTITUTIONPROCEDURE_LOAD_BY_ID,
    ACTION_INSTITUTIONPROCEDURE_LOAD_FAILURE,
    ACTION_INSTITUTIONPROCEDURE_LOAD_SUCCESS,

    ACTION_INSTITUTIONPROCEDURE_SAVE,
    ACTION_INSTITUTIONPROCEDURE_SAVE_SUCCESS,
    ACTION_INSTITUTIONPROCEDURE_SAVE_FAILURE,

    ACTION_INSTITUTIONPROCEDURE_VALIDATE,
    ACTION_INSTITUTIONPROCEDURE_VALIDATE_SUCCESS,
    ACTION_INSTITUTIONPROCEDURE_VALIDATE_FAILURE,

    ACTION_INSTITUTIONPROCEDURE_CREATE,
    ACTION_INSTITUTIONPROCEDURE_CREATE_SUCCESS,
    ACTION_INSTITUTIONPROCEDURE_CREATE_FAILURE,

    ACTION_INSTITUTIONPROCEDURE_SYNC,
    ACTION_INSTITUTIONPROCEDURE_SYNC_SUCCESS,
    ACTION_INSTITUTIONPROCEDURE_SYNC_FAILURE,

    ACTION_INSTITUTIONPROCEDURE_CLEAR,
    ACTION_INSTITUTIONPROCEDURE_FILTER,

    InstitutionProcedureAction
} from '../actions/institutionProcedure';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IInstitutionProcedureState {
    formData: Dtos.InstitutionProcedure | Dtos.InstitutionProcedure[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    validateState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.InstitutionProcedure[];
    formLength: number;
}

const initialState: IInstitutionProcedureState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: IInstitutionProcedureState, _isLoading: boolean): IInstitutionProcedureState {
    const newState: IInstitutionProcedureState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: IInstitutionProcedureState,
    formData: Dtos.InstitutionProcedure | Dtos.InstitutionProcedure[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IInstitutionProcedureState {
    const newState: IInstitutionProcedureState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: IInstitutionProcedureState, responseStatus: Dtos.ResponseStatus): IInstitutionProcedureState {
    const newState: IInstitutionProcedureState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: IInstitutionProcedureState, formFilter: IFormFilter): IInstitutionProcedureState {

    const items: Dtos.InstitutionProcedure[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (
            formFilter.search == "" ||
            p.name.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1
        )) : undefined;

    const newState: IInstitutionProcedureState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: IInstitutionProcedureState, propertiesToClear?: string[]): IInstitutionProcedureState {

    if (!propertiesToClear) {
        const newState: IInstitutionProcedureState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IInstitutionProcedureState, _isSaving: boolean): IInstitutionProcedureState {
    const newState: IInstitutionProcedureState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IInstitutionProcedureState,
    formData: Dtos.InstitutionProcedure | Dtos.InstitutionProcedure[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IInstitutionProcedureState {
    const newState: IInstitutionProcedureState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IInstitutionProcedureState, responseStatus: Dtos.ResponseStatus): IInstitutionProcedureState {
    const newState: IInstitutionProcedureState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function validating(state: IInstitutionProcedureState, _isValidating: boolean): IInstitutionProcedureState {
    const newState: IInstitutionProcedureState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: IInstitutionProcedureState,
    formData: Dtos.InstitutionProcedure | Dtos.InstitutionProcedure[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IInstitutionProcedureState {
    const newState: IInstitutionProcedureState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: IInstitutionProcedureState, responseStatus: Dtos.ResponseStatus): IInstitutionProcedureState {
    const newState: IInstitutionProcedureState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IInstitutionProcedureState, _isCreating: boolean): IInstitutionProcedureState {
    const newState: IInstitutionProcedureState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IInstitutionProcedureState,
    formData: Dtos.InstitutionProcedure,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IInstitutionProcedureState {
    const newState: IInstitutionProcedureState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IInstitutionProcedureState, responseStatus: Dtos.ResponseStatus): IInstitutionProcedureState {
    const newState: IInstitutionProcedureState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

const InstitutionProcedureReducer: Reducer<IInstitutionProcedureState> = (state: IInstitutionProcedureState = initialState, action: InstitutionProcedureAction) => {
    switch (action.type) {
        case ACTION_INSTITUTIONPROCEDURES_LOAD_BY_INSTITUTION_ID:
        case ACTION_INSTITUTIONPROCEDURE_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_INSTITUTIONPROCEDURE_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_INSTITUTIONPROCEDURE_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONPROCEDURE_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_INSTITUTIONPROCEDURE_SAVE:
            return saving(state, true);
        case ACTION_INSTITUTIONPROCEDURE_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_INSTITUTIONPROCEDURE_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONPROCEDURE_VALIDATE:
            return validating(state, true);
        case ACTION_INSTITUTIONPROCEDURE_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_INSTITUTIONPROCEDURE_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONPROCEDURE_CREATE:
            return creating(state, true);
        case ACTION_INSTITUTIONPROCEDURE_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_INSTITUTIONPROCEDURE_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONPROCEDURE_FILTER:
            return filter(state, action.formFilter);
        case ACTION_INSTITUTIONPROCEDURE_SYNC:
            return loading(state, true);
        case ACTION_INSTITUTIONPROCEDURE_SYNC_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_INSTITUTIONPROCEDURE_SYNC_FAILURE:
            return loadFailure(state, action.responseStatus);

    }

    return state;
}

export default InstitutionProcedureReducer;